import { useEffect, useRef, useState } from 'react'
import { type AppLocale, supportedLocales } from '@/config/locale'
import { useTranslation } from '@/i18n'

type LanguagePickerProps = {
  compact?: boolean
  locale?: AppLocale
  onChange?: (locale: AppLocale) => void
  label?: string
}

const languageLabels: Record<AppLocale, string> = {
  nl: 'Nederlands',
  en: 'Engels',
  fr: 'Frans',
  es: 'Spaans',
  de: 'Duits',
}

export function LanguagePicker({ compact = false, locale: controlledLocale, onChange, label }: LanguagePickerProps) {
  const translation = useTranslation()
  const locale = controlledLocale ?? translation.locale
  const setLocale = onChange ?? translation.setLocale
  const [isOpen, setIsOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const currentLabel = languageLabels[locale]
  const pickerLabel = label ?? translation.t('Taal')

  return (
    <div ref={pickerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={pickerLabel}
        className={[
          'flex items-center gap-2 rounded-full text-surface-500 transition hover:bg-white/[0.05] hover:text-surface-200',
          compact ? 'px-2 py-1 text-[0.68rem]' : 'border border-white/[0.08] bg-[#0d1113] px-3.5 py-2.5 text-sm',
        ].join(' ')}
      >
        <span>{pickerLabel}</span>
        <span className="font-semibold uppercase tracking-[0.12em] text-surface-300">{locale}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className={['h-3 w-3 transition-transform', isOpen ? 'rotate-180' : ''].join(' ')}
          fill="none"
        >
          <path d="m4 6 4 4 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        </svg>
      </button>

      {isOpen ? (
        <div
          role="listbox"
          aria-label={pickerLabel}
          className="absolute right-0 top-full z-50 mt-2 min-w-44 overflow-hidden rounded-[0.9rem] border border-white/[0.09] bg-[#0d1113]/95 p-1.5 shadow-[0_18px_45px_rgba(0,0,0,0.42)] backdrop-blur-xl"
        >
          {supportedLocales.map((option) => {
            const isActive = option === locale
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setLocale(option)
                  setIsOpen(false)
                }}
                className={[
                  'flex w-full items-center justify-between rounded-[0.65rem] px-3 py-2 text-left text-xs transition',
                  isActive ? 'bg-white/[0.08] text-white' : 'text-surface-400 hover:bg-white/[0.05] hover:text-surface-100',
                ].join(' ')}
              >
                <span>{languageLabels[option]}</span>
                <span className="ml-4 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-surface-500">{option}</span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
